import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import Header from '../components/Header'
import { ImagesModal } from '../components/quotation'
import {
  AssistantPreview,
  ChatInput,
  AttachedFiles,
  DragOverlay,
  ErrorMessage
} from '../components/assistant'
import { useAssistantFlow } from '../hooks/useAssistantFlow'
import { useFileUpload } from '../hooks/useFileUpload'
import OpportunityDecisionModal from '../components/OpportunityDecisionModal'
import { colors, borderRadius } from '../styles/tokens'

/**
 * Página do Assistente de Cotação
 *
 * Refatorado seguindo Single Responsibility Principle.
 * Cada componente tem uma única responsabilidade.
 *
 * Fluxo: chat → preview → images → redirect
 */
export default function AssistantQuotation() {
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState('')

  const {
    files,
    isDragging,
    fileInputRef,
    handleFilesChange,
    handleRemoveFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    clearFiles,
    openFilePicker
  } = useFileUpload()

  const {
    step,
    preview,
    clientName,
    contactId,
    loading,
    error,
    correction,
    refining,
    profile,
    issues,
    imageUrl1,
    imageUrl2,
    existingImages,
    hotelImages,
    carImages,
    tourImages,
    setStep,
    setClientName,
    setContactId,
    setCorrection,
    setImageUrl1,
    setImageUrl2,
    handleSubmit,
    handleCorrection,
    handleConfirmPreview,
    handleImagesSubmit,
    handleImageChange,
    validateImages,
    resetFlow,
    decisaoOportunidade,
    decidindoOportunidade,
    decidirOportunidade,
    cancelarOportunidade
  } = useAssistantFlow()

  const [creatingTemplate, setCreatingTemplate] = useState(null)

  const TEMPLATE_LABELS = {
    passport: 'Passaporte',
    visa_new: 'Visto — 1ª Via',
    visa_renewal_no_interview: 'Renovação s/ Entrevista',
    visa_renewal_interview: 'Renovação c/ Entrevista'
  }

  // Handler para selecionar template — sempre vem do chat com contactId
  const handleSelectTemplate = async (templateKey) => {
    setCreatingTemplate(templateKey)
    try {
      // Produto de catálogo não passa pela decisão de oportunidade — o
      // backend responde com `pode_perguntar=False`. Fluxo idêntico ao
      // anterior, sem modal.
      const result = await api.createQuoteFromTemplate(templateKey, {
        seller_id: profile?.seller_id,
        client_name: clientName || 'Cliente',
        contact_id: contactId
      })
      if (result.id) {
        navigate(`/app/cotacao/${result.id}`)
      }
    } catch (err) {
      console.error('Erro ao criar template:', err)
    } finally {
      setCreatingTemplate(null)
    }
  }

  // Handler para produto rápido Aéreo — navega para estação de voos com contact_id
  const handleSelectAereo = () => {
    const params = new URLSearchParams()
    if (contactId) params.set('contact_id', contactId)
    if (clientName) params.set('contact_name', clientName)
    navigate(`/app/aereo?${params.toString()}`)
  }

  const onSubmit = () => { handleSubmit(prompt, files) }
  const onReset = () => { resetFlow(); setPrompt(''); clearFiles() }
  const onFilesChange = (e) => { handleFilesChange(e) }

  const quickBtnStyle = {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    padding: '0.75rem 1rem',
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: creatingTemplate ? 'wait' : 'pointer',
    transition: 'all 0.15s'
  }

  return (
    <div
      style={{ minHeight: '100vh', backgroundColor: colors.background, fontFamily: "'Inter', sans-serif" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <OpportunityDecisionModal
        decisao={decisaoOportunidade} busy={decidindoOportunidade}
        onDecide={decidirOportunidade} onCancel={cancelarOportunidade}
      />
      <Header subtitle="Assistente de Cotação" userName={profile?.name || 'Usuário'} />
      <DragOverlay visible={isDragging} />

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <button
          onClick={() => navigate("/app")}
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            marginBottom: "1rem", padding: "0.5rem 1rem",
            backgroundColor: "transparent", border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.md, color: colors.text.secondary,
            fontSize: "0.875rem", cursor: "pointer"
          }}
        >
          ← Voltar para cotações
        </button>

        <div style={{
          backgroundColor: colors.card, borderRadius: borderRadius.lg,
          border: `1px solid ${colors.border}`, overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem', borderBottom: `1px solid ${colors.border}`,
            backgroundColor: 'rgba(79, 70, 229, 0.05)'
          }}>
            <p style={{ margin: 0, color: colors.text.primary }}>
              💬 Olá! Me conta sobre a cotação que você quer criar.
            </p>
            <p style={{ margin: '0.5rem 0 0', color: colors.text.secondary, fontSize: '0.875rem' }}>
              Ex: "pacote bariloche 2 adultos com voo hotel e passeios"
            </p>
          </div>

          {step === 'preview' && preview && (
            <AssistantPreview
              preview={preview}
              clientName={clientName}
              setClientName={setClientName}
              contactId={contactId}
              setContactId={setContactId}
              correction={correction}
              setCorrection={setCorrection}
              refining={refining}
              loading={loading}
              onCorrection={handleCorrection}
              onConfirm={handleConfirmPreview}
              onReset={onReset}
              issues={issues}
            />
          )}

          <ErrorMessage error={error} />

          {step === 'chat' && (
            <AttachedFiles files={files} onRemove={handleRemoveFile} />
          )}

          {step === 'chat' && (
            <ChatInput
              prompt={prompt}
              setPrompt={setPrompt}
              files={files}
              loading={loading}
              fileInputRef={fileInputRef}
              onSubmit={onSubmit}
              onOpenFilePicker={openFilePicker}
            />
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={onFilesChange}
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>

          {/* ═══ Produtos Rápidos ═══ */}
          <div style={{
            marginBottom: '1.5rem', backgroundColor: colors.card,
            borderRadius: borderRadius.lg, border: `1px solid ${colors.border}`,
            padding: '1.25rem', textAlign: 'left'
          }}>
            <p style={{
              fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: colors.text.muted, margin: '0 0 0.75rem 0'
            }}>
              Produtos Rápidos
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>

              {/* ✈️ Aéreo — novo produto rápido */}
              <button
                onClick={handleSelectAereo}
                disabled={!!creatingTemplate}
                style={quickBtnStyle}
              >
                <span style={{ fontSize: '1.25rem' }}>✈️</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>
                    Aéreo
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>Buscar voos</p>
                </div>
              </button>

              {/* Passaporte */}
              <button onClick={() => handleSelectTemplate('passport')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>📘</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Passaporte</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 407,25</p>
                </div>
              </button>

              {/* Visto — Primeira Via */}
              <button onClick={() => handleSelectTemplate('visa_new')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>🇺🇸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Visto — 1ª Via</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 1.850</p>
                </div>
              </button>

              {/* Visto — Renovação sem entrevista */}
              <button onClick={() => handleSelectTemplate('visa_renewal_no_interview')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>🇺🇸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Renovação s/ Entrevista</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 1.850</p>
                </div>
              </button>

              {/* Visto — Renovação com entrevista */}
              <button onClick={() => handleSelectTemplate('visa_renewal_interview')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>🇺🇸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Renovação c/ Entrevista</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 1.850</p>
                </div>
              </button>

            </div>
          </div>

          <button
            onClick={() => navigate('/app/nova')}
            style={{
              background: 'none', border: 'none', color: colors.text.secondary,
              cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline'
            }}
          >
            Usar fluxo tradicional
          </button>
        </div>
      </main>

      {step === 'images' && (
        <ImagesModal
          destination={preview?.destination}
          imageUrl1={imageUrl1}
          imageUrl2={imageUrl2}
          setImageUrl1={setImageUrl1}
          setImageUrl2={setImageUrl2}
          existingImages={existingImages}
          hotelImages={hotelImages}
          carImages={carImages}
          tourImages={tourImages}
          onImageChange={handleImageChange}
          onSubmit={handleImagesSubmit}
          onBack={() => setStep('preview')}
          loading={loading}
          isValid={validateImages()}
        />
      )}
    </div>
  )
}
